<script setup>
import { computed } from 'vue';
import { useApiData } from '../../composables/useApiData';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
  widgetStyle: {
    type: Object,
    default: () => ({}),
  },
});

const { accountId } = useAccount();

const { data, loading, error } = useApiData(
  computed(() => props.widget),
  computed(() => accountId.value)
);

const title = computed(() => props.widget.title || 'Orders');
const orders = computed(() => {
  if (data.value?.mapped?.orders && Array.isArray(data.value.mapped.orders))
    return data.value.mapped.orders;
  if (data.value?.raw?.orders && Array.isArray(data.value.raw.orders))
    return data.value.raw.orders;
  if (data.value?.raw && Array.isArray(data.value.raw)) return data.value.raw;
  return props.widget.properties?.orders || [];
});

const displayedOrders = computed(() => orders.value.slice(0, 6));

const formatPrice = price => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(price || 0);
};

const formatDate = date => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const getStatusColor = status => {
  const colors = {
    completed: 'bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.4)]',
    pending: 'bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.4)]',
    shipped: 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.4)]',
    cancelled: 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.4)]',
  };
  return colors[status] || 'bg-slate-300';
};
</script>

<template>
  <div class="h-full flex flex-col group font-inter">
    <div v-if="title" class="mb-8 flex items-center justify-between">
      <h3 class="text-xl font-black text-slate-900 tracking-tight">
        {{ title }}
      </h3>
      <button
        class="text-[12px] font-black text-slate-400 hover:text-slate-900 transition-colors uppercase tracking-widest"
      >
        View All
      </button>
    </div>

    <div v-if="loading" class="flex-1 flex items-center justify-center py-20">
      <div
        class="w-10 h-10 border-4 border-slate-100 border-t-slate-900 rounded-full animate-spin"
      />
    </div>

    <div
      v-else-if="error"
      class="flex-1 flex flex-col items-center justify-center p-10 text-center"
    >
      <span class="i-lucide-alert-circle text-3xl text-red-500 mb-2" />
      <p class="text-[13px] font-bold text-slate-500">{{ error }}</p>
    </div>

    <div v-else class="flex-1 space-y-4">
      <div
        v-for="order in displayedOrders"
        :key="order.id"
        class="group/item flex items-center justify-between p-5 hover:bg-slate-50/80 rounded-[32px] transition-all duration-300 cursor-pointer"
      >
        <div class="flex items-center gap-5 flex-1 min-w-0">
          <div
            class="w-14 h-14 rounded-[22px] bg-slate-100 overflow-hidden shadow-sm group-hover/item:scale-110 group-hover/item:shadow-lg transition-all duration-500"
          >
            <img
              :src="`https://api.dicebear.com/7.x/avataaars/svg?seed=${order.customer_name || order.id}`"
              class="w-full h-full object-cover"
            />
          </div>
          <div class="min-w-0">
            <p
              class="text-[15px] font-black text-slate-900 tracking-tight truncate mb-0.5"
            >
              {{ order.customer_name || 'Anonymous User' }}
            </p>
            <p
              class="text-[11px] font-bold text-slate-400 uppercase tracking-widest"
            >
              {{ formatDate(order.created_at) }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-12 text-right">
          <div class="flex items-center gap-3">
            <div
              class="w-2.5 h-2.5 rounded-full"
              :class="getStatusColor(order.status)"
            />
            <span class="text-[13px] font-bold text-slate-900 capitalize">{{
              order.status || 'Processed'
            }}</span>
          </div>
          <div class="min-w-[100px]">
            <p class="text-[16px] font-black text-slate-900 tracking-tighter">
              + {{ formatPrice(order.total) }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="displayedOrders.length === 0" class="py-20 text-center">
        <div
          class="w-16 h-16 rounded-[24px] bg-slate-50 flex items-center justify-center mx-auto mb-4 border border-slate-100"
        >
          <span class="i-lucide-shopping-bag text-slate-200 text-3xl" />
        </div>
        <p class="text-[13px] font-bold text-slate-400">No orders found yet</p>
      </div>
    </div>
  </div>
</template>
